import * as React from 'react'
import {
  Branch,
  Commit,
  ContentRequest,
  getRepoSpaceName,
} from '@basehub/data/replicache/index'
import { EventLayout } from './branch-timeline'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invariant } from '@epic-web/invariant'
import { Button } from '~/sotano-kit/primitives/button'
import { MergeIcon } from '~/components/icons/branch-icon'
import { CheckIcon, UpdateIcon } from '@radix-ui/react-icons'
import Link from 'next/link'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useBranchMergeDiff } from '../_hooks/use-merge-diff'
import { useConflictsWizard } from '../_hooks/use-active-conflicts-wizard'
import { useUserData } from '~/hooks-data/use-user-data'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { useUserStore } from '~/app/_store/user-store'
import { LabeledCheckbox } from '~/sotano-kit/primitives/checkbox'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { isUserRepo } from '@basehub/data/replicache/mutators/_public-types'
import { clsx } from 'clsx'
import { ArchiveIcon } from '~/components/icons/archive'
import {
  useIsEditorOrHigher,
  useIsExternalVisitor,
  useIsGuest,
  useIsGuestInHisBranch,
} from '~/hooks-data/use-user-role'
import { useRouter } from 'next/navigation'
import { useDefaultBranch } from '~/lib/commits/use-default-branch'
import { TimelineTabIcon } from './timeline/tab-icon'
import { getCommitBlockTreeManagerQueryKey } from '~/hooks-data/blocks-tree'
import { getCommitBlockTreeManagerQueryFn } from '~/hooks-data/blocks-tree/query'
import { CommitError } from '@basehub/data/replicache/mutators/repo-space/commit/types'
import { useReplicacheParamsStore } from '~/replicache/frontend/init'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { getTabKey } from '../../explore/components/tabs/store'
import { getURL } from '@basehub/shared-constants'
import { getClerkSessionFromCookie } from '~/replicache/frontend/get-clerk-session-from-cookie'

const BranchMergedBanner = ({ branch }: { branch: Branch }) => {
  const replicache = useRepoReplicache()?.data?.replicache
  const user = useUserStore((s) => s.optimisticUser)
  const userId = user?.id
  const username = user?.username
  const router = useRouter()
  const { data: defaultBranch } = useDefaultBranch()
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()

  return (
    <EventLayout
      icon={<CheckIcon className="text-button-label" />}
      accentColor="merged"
      align="center"
    >
      <div className="bg-dark-cr-merged rounded-lg p-4 flex items-center justify-between text-button-label font-medium">
        No changes to merge
        <Button
          unstyled
          focusStyled
          icon={<ArchiveIcon />}
          className="flex items-center gap-x-1 text-sm whitespace-nowrap font-medium cursor-default text-button-label/70 hover:text-button-label"
          onClick={async () => {
            if (!replicache || !branch || !userId || !username) return

            await replicache.mutate.toggleArchiveBranch({
              id: branch.id,
              archivedAt: new Date().toISOString(),
              archivedBy: userId,
              archivedByUsername: username,
            })
            router.push(
              `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
                defaultBranch?.name ?? 'main'
              )}/explore/timeline`
            )
          }}
        >
          Archive branch
        </Button>
      </div>
    </EventLayout>
  )
}

const BranchArchivedBanner = ({ branch }: { branch: Branch }) => {
  const replicache = useRepoReplicache()?.data?.replicache
  const username = useUserData(branch.archivedBy)?.data?.username
  const timeDiff = useTimeDiff(new Date(branch.archivedAt ?? ''))

  return (
    <EventLayout icon={<ArchiveIcon />} accentColor="archived" align="center">
      <div className="bg-dark-shade rounded-lg p-4 flex items-center justify-between font-medium">
        {username} archived this branch {timeDiff}
        <Button
          intent="secondary"
          onClick={async () => {
            if (!replicache || !branch || !username) return

            await replicache.mutate.toggleArchiveBranch({
              id: branch.id,
              archivedAt: new Date().toISOString(),
              archivedBy: '',
              archivedByUsername: '',
            })
          }}
        >
          Restore branch
        </Button>
      </div>
    </EventLayout>
  )
}

const CanNotMergeBranchBanner = ({ isGuest }: { isGuest: boolean }) => {
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const isExternalVisitor = useIsExternalVisitor()

  return (
    <EventLayout
      icon={<MergeIcon />}
      className="[&_div[data-icon]]:mt-3"
      accentColor="open"
      skipBorder={isExternalVisitor && !isGuestInHisBranch}
    >
      <div className="bg-dark-shade rounded-lg px-4 py-3 font-medium leading-normal">
        {isGuest ? 'Guests' : 'You'} don't have permission to merge branches.
        <p className="text-dark-muted font-normal text-sm mt-0.5">
          Use @tags in a comment to notify teammates so they can review and
          merge this branch.
        </p>
      </div>
    </EventLayout>
  )
}

export const useMergeMutation = () => {
  const { data } = useRepoReplicache()
  const user = useUserStore((s) => s.optimisticUser)
  const userId = user?.id
  const username = user?.username
  const router = useRouter()
  const { data: defaultBranch } = useDefaultBranch()
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      cr,
      type,
    }: {
      cr: ContentRequest
      type: 'merge' | 'update'
      archive: boolean
    }) => {
      invariant(data, 'Expected data')

      const requestPayload = {
        teamId: data.team.id,
        repoId: data.repoId,
        userId,
        contentRequestId: cr.id,
        sourceBranchId: cr.headBranchId,
        baseBranchId: cr.baseBranchId,
        type,
      }

      console.log('[useMergeMutation] Starting merge mutation', {
        type,
        contentRequestId: cr.id,
        sourceBranchId: cr.headBranchId,
        baseBranchId: cr.baseBranchId,
        userId,
        requestPayload,
      })

      const res = await fetch(
        getURL(
          'pump',
          process.env.NEXT_PUBLIC_ENVIRONMENT as 'production' | undefined
        ) + '/commit/merge',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-basehub-session': getClerkSessionFromCookie() || '',
          },
          body: JSON.stringify(requestPayload),
        }
      )

      console.log('[useMergeMutation] Received response', {
        status: res.status,
        statusText: res.statusText,
        type,
      })

      const result = (await res.json()) as {
        success: boolean
        error?: string
        errors?: CommitError[]
        commit?: Commit
        workingRootBlockId?: string
      }

      console.log('[useMergeMutation] Parsed response JSON', {
        success: result.success,
        hasError: !!result.error,
        hasErrors: !!result.errors,
        hasCommit: !!result.commit,
        commitId: result.commit?.id,
        workingRootBlockId: result.workingRootBlockId,
        type,
      })

      if (!result.success) {
        if (result.errors) {
          console.error('[useMergeMutation] Merge failed with errors', {
            errors: result.errors,
            type,
          })
          throw new Error(
            `Merge failed with errors: ${result.errors
              .map((e) => e.type)
              .join(', ')}`
          )
        }
        console.error('[useMergeMutation] Merge failed', {
          error: result.error,
          type,
        })
        throw new Error(result.error)
      }

      const commit = result.commit
      if (!commit) {
        console.error(
          '[useMergeMutation] Merge went through, but commit was not found in response.',
          { type }
        )
        return
      }

      console.log('[useMergeMutation] Merge successful, fetching commit tree', {
        commitId: commit.id,
        rootBlockId: commit.rootBlockId,
        type,
      })

      try {
        useReplicacheParamsStore.setState((p) => {
          if (!result.workingRootBlockId) return p
          return {
            repo: {
              ...p.repo,
              requestCommitRootBlockIds: Array.from(
                new Set([
                  ...(p.repo.requestCommitRootBlockIds ?? []),
                  result.workingRootBlockId,
                ])
              ),
            },
          }
        })
        await queryClient.fetchQuery(
          getCommitBlockTreeManagerQueryKey({
            commitId: commit.id,
            teamId: data.team.id,
            repoId: data.repoId,
          }),
          () =>
            getCommitBlockTreeManagerQueryFn({
              spaceId: getRepoSpaceName(data.team.id, data.repoId),
              commitId: commit.id,
              rootBlockId: commit.rootBlockId,
              replicache: data.replicache,
              forcePull: true,
            })
        )
        useReplicacheParamsStore.setState((p) => {
          if (!result.workingRootBlockId) return p
          return {
            repo: {
              ...p.repo,
              requestCommitRootBlockIds: (
                p.repo.requestCommitRootBlockIds ?? []
              ).filter((id) => id !== result.workingRootBlockId),
            },
          }
        })
        console.log('[useMergeMutation] Commit tree fetched successfully', {
          commitId: commit.id,
          type,
        })
      } catch (error) {
        console.error('[useMergeMutation] Error fetching commit tree', {
          error,
          commitId: commit.id,
          type,
        })
        // ignore this error, as this is not critical
      }

      console.log('[useMergeMutation] Merge mutation complete', {
        type,
        commitId: commit.id,
      })

      return result
    },
    {
      onSuccess: async (_, { cr, archive }) => {
        console.log('[useMergeMutation] onSuccess callback triggered', {
          archive,
          headBranchId: cr.headBranchId,
          userId,
        })

        if (!userId || !archive || !data || !username) return

        console.log('[useMergeMutation] Archiving branch after merge', {
          headBranchId: cr.headBranchId,
        })

        // Archive the branch after merging
        await data?.replicache.mutate.toggleArchiveBranch({
          id: cr.headBranchId,
          archivedAt: new Date().toISOString(),
          archivedBy: userId,
          archivedByUsername: username,
          triggerNotification: false,
        })

        console.log('[useMergeMutation] Branch archived, navigating to default branch', {
          headBranchId: cr.headBranchId,
          defaultBranchName: defaultBranch?.name,
        })

        router.push(
          `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
            defaultBranch?.name ?? 'main'
          )}/explore/timeline`
        )
      },
    }
  )

  return mutation
}

export const useBranchHref = ({
  branch,
}: {
  branch: Branch | undefined | null
}) => {
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()

  let href = `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
    branch?.name ?? ''
  )}`
  if (branch?.isInlineSuggestion) {
    href = `/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${getTabKey({
      type: 'suggestion',
      branchId: branch.id,
    })}`
  }

  return href
}

export const BranchLink = ({
  branch,
  className,
}: {
  branch: Branch | undefined | null
  className?: string
}) => {
  const href = useBranchHref({ branch })

  return (
    <Link
      href={href}
      className={clsx('hover:underline font-mono font-medium', className)}
    >
      {branch?.name}
    </Link>
  )
}

export const BranchActionsCTA = ({ branch }: { branch: Branch }) => {
  const mergeMutation = useMergeMutation()
  const { baseBranch, ahead, behind, status, cr } = useBranchMergeDiff({
    branch,
  })
  const replicache = useTeamReplicache()?.data?.replicache
  const { unresolvedCount, goToConflict, conflicts } = useConflictsWizard({
    branch,
  })
  const repo = useSelectedTeamAndRepo()?.data?.repo
  const isGuest = useIsGuest()
  const isEditorOrHigher = useIsEditorOrHigher()

  if (!cr) {
    if (!isEditorOrHigher) {
      return <CanNotMergeBranchBanner isGuest={isGuest ?? false} />
    }

    return null
  }

  const archiveNode = (
    <LabeledCheckbox
      checkboxProps={{
        checked: isUserRepo(repo) ? repo.branching?.archiveAfterMerging : true,
        onCheckedChange: async (checked: any) => {
          if (!replicache || !repo) return

          await replicache.mutate.updateRepositoryBranchingConfig({
            repoId: repo.id,
            config: { archiveAfterMerging: Boolean(checked) },
          })
        },
      }}
      className="ml-auto"
      label="Always archive after merging"
      name="archive-branch"
      horizontal="reverse"
    />
  )

  let icon: React.ReactNode
  let title: React.ReactNode
  let side: React.ReactNode
  let description: React.ReactNode
  let updateButton: {
    children: React.ReactNode
    onClick: () => void
    primary?: boolean
  } | null = null
  let isMergeButtonDisabled = false
  let accentColor: React.ComponentProps<typeof EventLayout>['accentColor'] =
    'open'

  if (isGuest || !isEditorOrHigher) {
    return <CanNotMergeBranchBanner isGuest={isGuest ?? false} />
  } else if (status === 'archived' && branch) {
    return <BranchArchivedBanner branch={branch} />
  } else if (status === 'conflicts') {
    icon = <TimelineTabIcon className="text-[18px]" />
    title = `This branch has conflicts that must be resolved`
    side = archiveNode
    accentColor = 'conflict'
    if (unresolvedCount > 0) {
      description = (
        <>
          {unresolvedCount === 1
            ? 'There is one conflict'
            : `There are ${unresolvedCount} conflicts`}{' '}
          between <BranchLink branch={branch} /> and{' '}
          <BranchLink branch={baseBranch} />.
        </>
      )
      updateButton = {
        children: (
          <span className="text-yellow-diff">
            Resolve {unresolvedCount}{' '}
            {unresolvedCount > 1 ? 'conflicts' : 'conflict'}
          </span>
        ),
        onClick: () => {
          const nextConflict = conflicts.find((c) => !c.resolved)
          if (nextConflict) {
            goToConflict(nextConflict)
          }
        },
      }
    } else {
      description = (
        <>
          All conflicts resolved between <BranchLink branch={branch} /> and{' '}
          <BranchLink branch={baseBranch} />. Ready to update.
        </>
      )
      updateButton = {
        primary: true,
        children: 'Update branch',
        onClick: () => {
          mergeMutation.mutate({ cr, type: 'update', archive: false })
        },
      }
    }
    isMergeButtonDisabled = true
  } else if (status === 'merged' && branch) {
    return <BranchMergedBanner branch={branch} />
  } else if (status === 'no-changes') {
    icon = <MergeIcon />
    title = `This branch doesn't have new commits for the base branch`
    description = <>No changes to merge.</>
    isMergeButtonDisabled = true
    if (!!behind) {
      updateButton = {
        children: 'Update branch',
        onClick: () => {
          mergeMutation.mutate({ cr, type: 'update', archive: false })
        },
      }
    }
  } else if (status === 'out-of-date') {
    icon = <UpdateIcon />
    title = `This branch is out-of-date with the base branch`
    side = archiveNode
    description = (
      <>
        Bring {behind} {behind === 1 ? 'commit' : 'commits'} from{' '}
        <BranchLink branch={baseBranch} /> into this branch.
      </>
    )
    updateButton = {
      children: 'Update branch',
      onClick: () => {
        mergeMutation.mutate({ cr, type: 'update', archive: false })
      },
    }
  } else {
    side = archiveNode
    accentColor = 'ready'
    icon = <MergeIcon />
    title = `This branch is ready to merge into ${baseBranch?.name}`
    description = (
      <>
        {/* Merge {ahead} {ahead === 1 ? 'commit' : 'commits'} to{' '} */}
        <BranchLink branch={baseBranch} className="text-dark-dim" /> will be
        updated with the {ahead}&nbsp;
        {ahead === 1 ? 'change' : 'changes'} you made in this branch.
      </>
    )
  }

  if (!title) return null
  return (
    <EventLayout
      icon={icon}
      accentColor={accentColor}
      className={clsx('[&_div[data-icon]]:mt-3 [&_div[data-border]]:top-3')}
    >
      <div className="border rounded-lg border-dark-border-solid overflow-hidden">
        <div className="p-4 flex items-center gap-2 justify-between">
          <div>
            <h3 className="font-medium text-dark-bright">{title}</h3>
            <p className="text-sm text-dark-muted">{description}</p>
          </div>
          {updateButton && (
            <div>
              <Button
                intent={updateButton.primary ? 'primary' : 'secondary'}
                onClick={() => {
                  updateButton.onClick()
                }}
                disabled={mergeMutation.isLoading}
              >
                {updateButton.children}
              </Button>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-dark-border-solid bg-dark-shade flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Button
              icon={<MergeIcon />}
              iconSide="left"
              intent={isMergeButtonDisabled ? 'secondary' : 'primary'}
              onClick={() => {
                if (!isUserRepo(repo)) return

                mergeMutation.mutate({
                  cr,
                  type: 'merge',
                  archive: repo.branching?.archiveAfterMerging ?? false,
                })
              }}
              disabled={mergeMutation.isLoading || isMergeButtonDisabled}
            >
              Merge to {baseBranch?.name}
            </Button>
            {mergeMutation.data && !mergeMutation.data.success && (
              <div className="text-xs text-dark-muted">
                {mergeMutation.data.error}
              </div>
            )}
          </div>
          <div className="text-xs text-dark-muted">
            {/* Merging will create a new commit in the base branch. */}
            {side}
          </div>
        </div>
      </div>
    </EventLayout>
  )
}
